import { useContext } from 'react';
import { SpoolDto } from '../../types/spool-local';
import spoolsContext from '../../../context/spools/spoolsContext';
import HighlightText from '../../HighlightText';
import InfoPopUp from '../../InfoPopUp';
import { UserIcon, IncentivizedArrowIcon, NotificationIcon } from '../../../assets/icons';
import {
    formatAmountWithSuffix,
    formatPercentages,
    formatUSD,
} from '../../../helpers/formats';
import IncentivizeReward from '../../../modals/infoPopUp/IncentivizeReward';
import AdjustedApy from '../../../modals/infoPopUp/AdjustedApy';
import { getRiskScore, mapRiskToRange } from '../../../helpers/sdkHelper';
import appContext from '../../../context/app/appContext';

type Props = {
    spoolDetails: SpoolDto;
    toggle: () => void;
    isActiveSpools: boolean;
};

const SpoolsAccordionRow: React.FC<Props> = ({
    spoolDetails,
    toggle,
    isActiveSpools,
}) => {
    const {
        name,
        adjustedApy,
        tvr,
        riskModelAddress,
        asset,
        riskTolerance,
        incentivizedRewards,
        performanceFees,
        baseApy,
        userData,
    } = spoolDetails;

    const { chainRiskModels } = useContext(appContext);

    const { searchTerm } = useContext(spoolsContext);

    return (
        <div className='row-container' onClick={toggle}>
            <div>
                <div className='arrow-container'>
                    <div className='arrow' />
                </div>
                <span>
                    {searchTerm ? (
                        <HighlightText text={name} highlight={searchTerm} />
                    ) : (
                        <span className='highlight-text'>{name}</span>
                    )}
                    {userData?.userClaimableAmount ||
                    userData?.pendingDepositAmount ||
                    userData?.pendingWithdrawalAmount ? (
                        <InfoPopUp
                            className='notification'
                            trigger={<NotificationIcon />}
                        >
                            {!!userData?.pendingDepositAmount && (
                                <>
                                    <span className='block mb'>
                                        Your Pending Deposits are not included in your
                                        Balance in the Spool.
                                    </span>
                                    <span className='block'>Pending Deposit:</span>
                                    <strong className='block mb'>
                                        {formatUSD(userData?.pendingDepositAmount || 0)}{' '}
                                        {asset.symbol}
                                    </strong>
                                </>
                            )}
                            {!!userData?.pendingWithdrawalAmount && (
                                <>
                                    <span className='block'>Pending Withdrawal:</span>
                                    <strong className='block mb'>
                                        {formatUSD(
                                            userData?.pendingWithdrawalAmount || 0
                                        )}{' '}
                                        {asset.symbol}
                                    </strong>
                                </>
                            )}
                            {!!userData?.userClaimableAmount && (
                                <>
                                    <span className='block'>Ready to Withdraw:</span>
                                    <strong>
                                        {formatUSD(userData?.userClaimableAmount || 0)}{' '}
                                        {asset.symbol}
                                    </strong>
                                </>
                            )}
                        </InfoPopUp>
                    ) : (
                        !isActiveSpools &&
                        userData &&
                        !!userData.userCurrentBalance && (
                            <InfoPopUp className='user' trigger={<UserIcon />}>
                                <span className='block'>
                                    You are participating in this Smart Vault
                                </span>
                            </InfoPopUp>
                        )
                    )}
                </span>
            </div>
            <div className='apy'>
                <span className='column-label'>APY</span>
                <div>
                    <IncentivizeReward
                        icon={
                            <IncentivizedArrowIcon
                                incentivized={!!incentivizedRewards?.length}
                            />
                        }
                        incentivizedRewards={incentivizedRewards}
                    />
                    {formatPercentages(parseFloat(adjustedApy) * 100 || 0)}%
                    <AdjustedApy
                        adjustedApy={adjustedApy}
                        baseApy={baseApy}
                        incentivizedRewards={incentivizedRewards}
                        performanceFees={performanceFees}
                    />
                </div>
            </div>
            <div>
                <span className='column-label'>{isActiveSpools ? 'Balance' : 'TVR'}</span>
                {tvr === '0'
                    ? 'N/A'
                    : '$' +
                          formatAmountWithSuffix(
                              isActiveSpools
                                  ? userData?.userCurrentBalance ?? 0
                                  : tvr || 0
                          ) || '0.00'}
            </div>
            <div>
                <span className='column-label'>Risk Model</span>
                {chainRiskModels[riskModelAddress]?.name}
            </div>
            <div>
                <span className='column-label'>Asset</span>
                {asset?.symbol}
            </div>

            <div>
                <span className='column-label'>Risk Appetite</span>
                {getRiskScore(+riskTolerance)} ({mapRiskToRange(+riskTolerance)})
            </div>
        </div>
    );
};

export default SpoolsAccordionRow;
